import getBrowserFingerprint from 'get-browser-fingerprint'
import { addBrowserId } from './apiCalls'
import { getItem, setItem } from './storageUtils'

// ─── Existing export (unchanged) ─────────────────────────────────────────────

export const browserFingerPrint = () => {
  const fingerprint = getBrowserFingerprint()
  const encodedValue = encodeURIComponent(`浏览器 ${fingerprint} ?`)
  if (!getItem('browser-key')) {
    setItem('browser-key', encodedValue)
    const data = {
      uniqueKey: encodedValue
    }
    addBrowserId(data)
  }
  return encodedValue
}

// ─── Helpers (all individually guarded) ──────────────────────────────────────

/**
 * Tiny guard: runs fn(), returns fallback on any error.
 * Ensures a single failing field can never crash the whole fingerprint.
 */
const _safeGet = (fn, fallback) => {
  try { return fn() } catch (e) { return fallback }
}

const _getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return 'canvas_not_supported'
    canvas.width = 200
    canvas.height = 50
    ctx.textBaseline = 'alphabetic'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('AM-Gaming \u{1F3AE}', 2, 15)
    ctx.fillStyle = 'rgba(102,204,0,0.7)'
    ctx.fillText('AM-Gaming \u{1F3AE}', 4, 17)
    return canvas.toDataURL()
  } catch (e) {
    return 'canvas_error'
  }
}

const _getWebGLInfo = () => {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) return { vendor: 'not_supported', renderer: 'not_supported', version: 'not_supported' }
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    return {
      vendor: dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
      renderer: dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      version: gl.getParameter(gl.VERSION)
    }
  } catch (e) {
    return { vendor: 'webgl_error', renderer: 'webgl_error', version: 'webgl_error' }
  }
}

/**
 * Fetches public IP with a 4-second timeout.
 * Uses AbortController instead of AbortSignal.timeout() for broad
 * browser compatibility (AbortSignal.timeout is Chrome 103+ only).
 * Always resolves — never rejects.
 */
const _getPublicIP = () => {
  return new Promise((resolve) => {
    const controller = new AbortController()
    const timer = setTimeout(() => {
      controller.abort()
      resolve('unknown')
    }, 4000)

    fetch('https://api.ipify.org?format=json', { signal: controller.signal })
      .then((res) => res.json())
      .then((json) => {
        clearTimeout(timer)
        resolve(json && json.ip ? json.ip : 'unknown')
      })
      .catch(() => {
        clearTimeout(timer)
        resolve('unknown')
      })
  })
}

const _parseUserAgent = () => {
  try {
    const ua = navigator.userAgent || ''
    let os = 'Unknown OS'
    let browser = 'Unknown'
    let browserVersion = 'Unknown'

    if (/Windows NT 10/.test(ua)) os = 'Windows 10/11'
    else if (/Windows NT 6\.3/.test(ua)) os = 'Windows 8.1'
    else if (/Windows NT 6\.1/.test(ua)) os = 'Windows 7'
    else if (/Windows/.test(ua)) os = 'Windows'
    else if (/Mac OS X/.test(ua)) os = ('macOS ' + ((ua.match(/Mac OS X ([\d_]+)/) || [])[1] || '').replace(/_/g, '.')).trim()
    else if (/Android/.test(ua)) os = ('Android ' + ((ua.match(/Android ([\d.]+)/) || [])[1] || '')).trim()
    else if (/iPhone OS/.test(ua)) os = ('iOS ' + ((ua.match(/iPhone OS ([\d_]+)/) || [])[1] || '').replace(/_/g, '.')).trim()
    else if (/Linux/.test(ua)) os = 'Linux'

    if (/Edg\//.test(ua)) {
      browser = 'Microsoft Edge'
      browserVersion = (ua.match(/Edg\/([\d.]+)/) || [])[1] || 'Unknown'
    } else if (/OPR\//.test(ua)) {
      browser = 'Opera'
      browserVersion = (ua.match(/OPR\/([\d.]+)/) || [])[1] || 'Unknown'
    } else if (/Chrome\//.test(ua)) {
      browser = 'Chrome'
      browserVersion = (ua.match(/Chrome\/([\d.]+)/) || [])[1] || 'Unknown'
    } else if (/Firefox\//.test(ua)) {
      browser = 'Firefox'
      browserVersion = (ua.match(/Firefox\/([\d.]+)/) || [])[1] || 'Unknown'
    } else if (/Safari\//.test(ua)) {
      browser = 'Safari'
      browserVersion = (ua.match(/Version\/([\d.]+)/) || [])[1] || 'Unknown'
    }

    return { os, browser, browserVersion }
  } catch (e) {
    return { os: 'parse_error', browser: 'parse_error', browserVersion: 'parse_error' }
  }
}

const _getConnectionInfo = () => {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (!conn) return { effectiveType: 'unknown', downlink: null, rtt: null, saveData: null }
    return {
      effectiveType: conn.effectiveType || 'unknown',
      downlink: conn.downlink != null ? conn.downlink : null,
      rtt: conn.rtt != null ? conn.rtt : null,
      saveData: conn.saveData != null ? conn.saveData : null
    }
  } catch (e) {
    return { effectiveType: 'unknown', downlink: null, rtt: null, saveData: null }
  }
}

const _getColorScheme = () => {
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light'
    return 'no-preference'
  } catch (e) {
    return 'unknown'
  }
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Collects a comprehensive browser/device fingerprint for fraud tracing.
 *
 * Safety guarantees (production-safe):
 *  - _safeGet wraps every individual field — one failing property cannot crash others.
 *  - The entire function body is wrapped in try/catch — if everything fails, a minimal
 *    fallback object is returned so the reset-password API call is never blocked.
 *  - _getPublicIP always resolves and never rejects (AbortController timeout).
 *
 * @returns {Promise<Object>}
 */
export const collectBrowserFingerprint = async () => {
  try {
    const { os, browser, browserVersion } = _parseUserAgent()
    const publicIPPromise = _getPublicIP() // start early — runs in parallel with sync work

    const data = {
      // ── Timestamps ─────────────────────────────────────────────────────────
      capturedAt: _safeGet(() => new Date().toISOString(), null),
      capturedAtLocal: _safeGet(() => new Date().toString(), null),

      // ── User-agent & Browser ───────────────────────────────────────────────
      userAgent: _safeGet(() => navigator.userAgent || null, null),
      browser,
      browserVersion,
      vendor: _safeGet(() => navigator.vendor || null, null),
      platform: _safeGet(() => navigator.platform || null, null),
      os,

      // ── Screen & Display ───────────────────────────────────────────────────
      screenWidth: _safeGet(() => window.screen.width, null),
      screenHeight: _safeGet(() => window.screen.height, null),
      screenAvailWidth: _safeGet(() => window.screen.availWidth, null),
      screenAvailHeight: _safeGet(() => window.screen.availHeight, null),
      screenColorDepth: _safeGet(() => window.screen.colorDepth, null),
      screenPixelDepth: _safeGet(() => window.screen.pixelDepth, null),
      devicePixelRatio: _safeGet(() => window.devicePixelRatio, null),
      viewportWidth: _safeGet(() => window.innerWidth, null),
      viewportHeight: _safeGet(() => window.innerHeight, null),

      // ── Locale & Language ──────────────────────────────────────────────────
      language: _safeGet(() => navigator.language || null, null),
      languages: _safeGet(() => Array.from(navigator.languages || []), []),
      locale: _safeGet(() => Intl.DateTimeFormat().resolvedOptions().locale || null, null),

      // ── Timezone ───────────────────────────────────────────────────────────
      timezone: _safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone || null, null),
      timezoneOffset: _safeGet(() => new Date().getTimezoneOffset(), null),

      // ── Hardware ───────────────────────────────────────────────────────────
      hardwareConcurrency: _safeGet(() => navigator.hardwareConcurrency != null ? navigator.hardwareConcurrency : null, null),
      deviceMemoryGB: _safeGet(() => navigator.deviceMemory != null ? navigator.deviceMemory : null, null),
      maxTouchPoints: _safeGet(() => navigator.maxTouchPoints != null ? navigator.maxTouchPoints : null, null),
      isTouchDevice: _safeGet(() => 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0, false),

      // ── Privacy Flags ──────────────────────────────────────────────────────
      cookiesEnabled: _safeGet(() => navigator.cookieEnabled != null ? navigator.cookieEnabled : null, null),
      doNotTrack: _safeGet(() => navigator.doNotTrack || window.doNotTrack || null, null),
      onLine: _safeGet(() => navigator.onLine != null ? navigator.onLine : null, null),

      // ── Navigation Context ─────────────────────────────────────────────────
      referrer: _safeGet(() => document.referrer || null, null),
      currentUrl: _safeGet(() => window.location.href, null),
      currentOrigin: _safeGet(() => window.location.origin, null),
      currentPath: _safeGet(() => window.location.pathname, null),
      currentSearch: _safeGet(() => window.location.search, null),
      historyLength: _safeGet(() => window.history.length, null),

      // ── Network ────────────────────────────────────────────────────────────
      connection: _getConnectionInfo(),

      // ── Browser Capability Flags ───────────────────────────────────────────
      javaEnabled: _safeGet(() => typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false, false),
      pdfViewerEnabled: _safeGet(() => navigator.pdfViewerEnabled != null ? navigator.pdfViewerEnabled : null, null),
      webdriver: _safeGet(() => navigator.webdriver != null ? navigator.webdriver : null, null), // true = bot/automation

      // ── Appearance ─────────────────────────────────────────────────────────
      preferredColorScheme: _getColorScheme(),

      // ── WebGL — GPU & driver (unique per device) ───────────────────────────
      webgl: _getWebGLInfo(),

      // ── Canvas Fingerprint — GPU rendering signature ───────────────────────
      canvasFingerprint: _getCanvasFingerprint(),

      // ── Browser Plugins ────────────────────────────────────────────────────
      plugins: _safeGet(() => Array.from(navigator.plugins || []).map(function (p) { return p.name }).filter(Boolean), []),

      // ── Stored App Identifiers ─────────────────────────────────────────────
      storedBrowserKey: _safeGet(() => getItem('browser-key') || null, null),
      storedUserId: _safeGet(() => getItem('userId') || null, null),
      storedLoggedIn: _safeGet(() => getItem('loggedIn') || null, null),

      // ── Storage Availability ───────────────────────────────────────────────
      localStorageAvailable: _safeGet(() => { localStorage.setItem('__fp', '1'); localStorage.removeItem('__fp'); return true }, false),
      sessionStorageAvailable: _safeGet(() => { sessionStorage.setItem('__fp', '1'); sessionStorage.removeItem('__fp'); return true }, false)
    }

    // Wait for IP (already running in parallel — minimal extra latency)
    data.publicIP = await publicIPPromise
    return data
  } catch (e) {
    // Absolute safety net: fingerprinting must NEVER throw or block the password reset
    return { error: 'fingerprint_collection_failed', capturedAt: new Date().toISOString() }
  }
}
